import {
    ActionType,
    ProFormInstance,
    ProTable,
    ProColumns
} from '@ant-design/pro-components'
import { Button, message, Modal, Tooltip, Tag } from 'antd'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useDebounceFn } from 'ahooks'
import { ExclamationCircleOutlined, QrcodeOutlined, SettingOutlined, DisconnectOutlined } from '@ant-design/icons'
import moment from 'moment'
import { StatusEnum } from '@/pages/constant'
import { StatusValueEnum } from '@/pages/constant'
import styles from './index.module.scss'
import addIcon from '@/assets/images/add.jpg'
import searchIcon from '@/assets/images/search-icon.jpg'
import channelIcon from '@/assets/images/channel.png'
import AddChannel from './components/add-channel'
import BindBot from './components/bind-bot'
import WhatsappConnect, { waStatusClass, waStatusTagClass } from './components/whatsapp-connect'
import { listWaAccounts, unbindWaAccount, waAccountStatusText } from '@/service/whatsappPersonal/api'
import type { WaAccountItem } from '@/service/whatsappPersonal/types'
import whatsappIcon from '@/assets/images/whatsapp.png'
import telegramIcon from '@/assets/images/telegram.png'
import livechatIcon from '@/assets/images/livechat.png'
import lineIcon from '@/assets/images/line.png'
import bindIcon from '@/assets/images/bind.png'
import rebindIcon from '@/assets/images/rebind.png'
import unbindIcon from '@/assets/images/bind-no.png'
import copyIcon from '@/assets/images/copy.png'
import offlineIcon from '@/assets/images/offline.png'
import onlineIcon from '@/assets/images/online.png'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import { deleteChannelApi } from '@/service/channel/api/deleteChannel'
import { updateChannelApi } from '@/service/channel/api/updateChannel'
import { offlineChannelApi } from '@/service/channel/api/offlineChannel'
import { onlineChannelApi } from '@/service/channel/api/onlineChannel'
import { copyToClipboard } from '@/utils/clipboard'

export type ChannelItem = ChannelItemRes

interface ChannelListParams {
    current?: number
    pageSize?: number
    channel_name?: string
    companyName?: string
}

const WA_PERSONAL = 'WhatsAppPersonal'

const typeIconMap: Record<string, string> = {
    WhatsApp: whatsappIcon,
    WhatsAppPersonal: whatsappIcon,
    Telegram: telegramIcon,
    WebWidget: livechatIcon,
    Line: lineIcon,
}

const Channel = () => {
    const actionRef = useRef<ActionType>()
    const formRef = useRef<ProFormInstance>()
    const { t } = useTranslation()
    const searchTimerRef = useRef<NodeJS.Timeout | null>(null)
    const [addChannelOpen, setAddChannelOpen] = useState(false)
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    const [bindBotOpen, setBindBotOpen] = useState(false)
    const [currentRecord, setCurrentRecord] = useState<ChannelItem | null>(null)
    // WhatsApp 个人号：channel_id -> {binding, account}，用于展示真实连接状态
    const [waAccounts, setWaAccounts] = useState<Record<string, WaAccountItem>>({})
    const [waModal, setWaModal] = useState<{ open: boolean; channelId: string | null; phase: 'connect' | 'config' }>({ open: false, channelId: null, phase: 'connect' })
    // 解绑中 / 结果待确认的账号
    const [waUnbinding, setWaUnbinding] = useState<Record<string, 'loading' | 'uncertain'>>({})

    const refreshWaAccounts = async () => {
        try {
            const list = await listWaAccounts()
            const map: Record<string, WaAccountItem> = {}
            list.forEach((it) => { map[it.binding.channel_id] = it })
            setWaAccounts(map)
        } catch (e: any) {
            // 不阻断渠道列表，只提示
            if (e?.status === 401) message.warning('WhatsApp management key is invalid, WhatsApp status is unavailable')
        }
    }

    const openWaModal = (channelId: string, phase: 'connect' | 'config') => {
        setWaModal({ open: true, channelId, phase })
    }

    const handleWaUnbind = (record: ChannelItem) => {
        Modal.confirm({
            title: (
                <div style={{ textAlign: 'center' }}>
                    Unbind <span className="delete-channel-title-highlight">WhatsApp</span>
                </div>
            ),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center' }}>
                    <span className="delete-channel-content-highlight">{record.channel_name}</span> will log out of the WhatsApp linked device and the channel binding will be removed. Chat history is kept. This account ID cannot be reused.
                </div>
            ),
            okText: 'Unbind',
            cancelText: 'Cancel',
            centered: true,
            width: 400,
            className: 'delete-channel-modal',
            okButtonProps: { danger: true },
            onOk: () => doWaUnbind(record),
        })
    }

    const doWaUnbind = async (record: ChannelItem) => {
        const id = record.channel_id
        if (waUnbinding[id] === 'loading') return
        if (waModal.channelId === id) setWaModal({ open: false, channelId: null, phase: 'connect' })
        setWaUnbinding((p) => ({ ...p, [id]: 'loading' }))
        try {
            await unbindWaAccount(id)
            message.success('WhatsApp account unbound')
            setWaUnbinding((p) => { const n = { ...p }; delete n[id]; return n })
            actionRef.current?.reload()
        } catch (e: any) {
            const tip = e?.requestId ? ` (request_id: ${e.requestId})` : ''
            // 超时 / 502 / 500：结果不确定，保持"待确认"，允许同一个 DELETE 重试
            setWaUnbinding((p) => ({ ...p, [id]: 'uncertain' }))
            message.error(`Unbind result is not confirmed, please retry. ${e?.message || ''}${tip}`)
        }
    }

    const { run: handleSearch } = useDebounceFn(
        () => {
            formRef.current?.submit()
        },
        { wait: 500 }
    )

    const handleDelete = (record: ChannelItem) => {
        Modal.confirm({
            title: (
                <div style={{ textAlign: 'center' }}>
                    Delete <span className="delete-channel-title-highlight">Channel</span>
                </div>
            ),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center' }}>
                    Are you sure you want to delete <span className="delete-channel-content-highlight">{record.channel_name}</span>? You can't undo this action afterwards.
                </div>
            ),
            okText: 'Delete',
            cancelText: 'Cancel',
            centered: true,
            width: 360,
            className: 'delete-channel-modal',
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await deleteChannelApi({ id: record.id })
                if (res.code === 0) {
                    message.success('Channel deleted successfully')
                    actionRef.current?.reload()
                } else {
                    message.error(res.message || 'Failed to delete channel')
                }
            },
        })
    }

    const handleUnbind = (record: ChannelItem) => {
        Modal.confirm({
            title: (
                <div style={{ textAlign: 'center' }}>
                    Unbind <span className="delete-channel-title-highlight">Bot</span>
                </div>
            ),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center' }}>
                    Are you sure you want to unbind the bot from <span className="delete-channel-content-highlight">{record.channel_name}</span>?
                </div>
            ),
            okText: 'Unbind',
            cancelText: 'Cancel',
            centered: true,
            width: 360,
            className: 'delete-channel-modal',
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await updateChannelApi({
                    id: record.id,
                    robot_id: 0,
                    robot_version_id: 0,
                    robot_name: '',
                })
                if (res.code === 0) {
                    message.success('Bot unbound successfully')
                    actionRef.current?.reload()
                } else {
                    message.error(res.message || 'Failed to unbind bot')
                }
            },
        })
    }

    const handleOffline = (record: ChannelItem) => {
        Modal.confirm({
            title: (
                <div style={{ textAlign: 'center' }}>
                    Offline <span className="delete-channel-title-highlight">Channel</span>
                </div>
            ),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center' }}>
                    Are you sure you want to offline <span className="delete-channel-content-highlight">{record.channel_name}</span>?
                </div>
            ),
            okText: 'Offline',
            cancelText: 'Cancel',
            centered: true,
            width: 360,
            className: 'delete-channel-modal',
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await offlineChannelApi({
                    channel: record.channel,
                    channel_id: record.channel_id,
                })
                if (res.code === 0) {
                    message.success('Offline successfully')
                    actionRef.current?.reload()
                } else {
                    message.error(res.message || 'Failed to offline channel')
                }
            },
        })
    }

    const handleOnline = (record: ChannelItem) => {
        Modal.confirm({
            title: (
                <div style={{ textAlign: 'center' }}>
                    Online <span className="delete-channel-title-highlight">Channel</span>
                </div>
            ),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center' }}>
                    Are you sure you want to online <span className="delete-channel-content-highlight">{record.channel_name}</span>?
                </div>
            ),
            okText: 'Online',
            cancelText: 'Cancel',
            centered: true,
            width: 360,
            className: 'delete-channel-modal',
            onOk: async () => {
                const res = await onlineChannelApi({
                    channel: record.channel,
                    channel_id: record.channel_id,
                })
                if (res.code === 0) {
                    message.success('Online successfully')
                    actionRef.current?.reload()
                } else {
                    message.error(res.message || 'Failed to online channel')
                }
            },
        })
    }

    const renderWaOperations = (record: ChannelItem) => {
        const id = record.channel_id
        const unbinding = waUnbinding[id]
        const st = waAccounts[id]?.account?.status
        if (unbinding) {
            return [
                <Button key="retry-unbind" type="link" size="small" danger loading={unbinding === 'loading'} onClick={(e) => { e.stopPropagation(); doWaUnbind(record) }}>
                    Retry unbind
                </Button>,
            ]
        }
        return [
            <Button key="connect" type="text" onClick={(e) => { e.stopPropagation(); openWaModal(id, 'connect') }}>
                <Tooltip title={st === 'WORKING' ? 'Connection' : 'Connect / Scan QR'} className={styles.tooltip_custom}>
                    <QrcodeOutlined style={{ color: '#5793FA', fontSize: 15, marginLeft: 20 }} />
                </Tooltip>
            </Button>,
            <Button key="config" type="text" onClick={(e) => { e.stopPropagation(); openWaModal(id, 'config') }}>
                <Tooltip title="Groups & Bot" className={styles.tooltip_custom}>
                    <SettingOutlined style={{ color: '#5793FA', fontSize: 15 }} />
                </Tooltip>
            </Button>,
            <Button key="unbind" type="text" onClick={(e) => { e.stopPropagation(); handleWaUnbind(record) }}>
                <Tooltip title="Unbind" className={styles.tooltip_custom}>
                    <DisconnectOutlined style={{ color: '#F5684F', fontSize: 15 }} />
                </Tooltip>
            </Button>,
        ]
    }

    const columns: ProColumns<ChannelItem>[] = [
        {
            title: 'Channel Name',
            dataIndex: 'channel_name',
            ellipsis: true,
            width: '15%',
            minWidth: 150,
            search: { title: false } as any,
            fieldProps: {
                placeholder: 'Search Channel',
                onChange: () => {
                    handleSearch()
                },
                prefix: (
                    <img
                        src={searchIcon}
                        alt="search"
                        style={{ width: 14, height: 14 }}
                    />
                ),
                style: {
                    borderRadius: '10px',
                    width: '240px',
                    backgroundColor: '#090A0D',
                    color: '#E5E8EF',
                    border: '1px solid #1E212B',
                    '--ant-color-text-placeholder': '#515766',
                    '&:hover': {
                        borderColor: '#5793FA'
                    }
                },
            },
            render: (dom, entity) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    width: '100%'
                }}>
                    <img
                        src={channelIcon}
                        style={{
                            width: 16,
                            height: 16,
                            marginRight: 3,
                            flexShrink: 0
                        }}
                    />
                    <span
                        className={styles['ellipsis-text']}
                        style={{
                            minWidth: 0,
                            flex: 1
                        }}
                    >
                        {dom}
                    </span>
                </div>
            ),
        },
        {
            title: 'Country',
            dataIndex: 'country',
            search: false,
            minWidth: 120,
            valueEnum: {
                'Indonesia': { text: 'Indonesia' },
                'Malaysia': { text: 'Malaysia' },
                'United States': { text: 'United States of America' },
                'Philippines': { text: 'Philippines' },
                'China': { text: 'China' },
                'Thailand': { text: 'Thailand' },
                'Vietnam': { text: 'Vietnam' },
                'South Korea': { text: 'Korea' },
                'Japan': { text: 'Japan' },
                'France': { text: 'France' },
                'Bangladesh': { text: 'Bangladesh' },
                'Mexico': { text: 'Mexico' },
                'Columbia': { text: 'Columbia' },
                'Brazil': { text: 'Brazil' },
                'Spain': { text: 'Spain' },
                'India': { text: 'India' },
            },
        },
        {
            title: 'Type',
            dataIndex: 'channel',
            search: false,
            minWidth: 160,
            render: (_, record) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {typeIconMap[record.channel] && (
                        <img
                            src={typeIconMap[record.channel]}
                            alt={record.channel}
                            style={{ width: 20, height: 20, flexShrink: 0 }}
                        />
                    )}
                    <span>
                        {record.channel}
                    </span>
                </span>
            ),
        },
        {
            title: 'Channel ID',
            dataIndex: 'channel_id',
            search: false,
            ellipsis: true,
            minWidth: 160,
            render: (_, record) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
                    <span className={styles['ellipsis-text']} style={{ minWidth: 0 }}>
                        {record.channel_id}
                    </span>
                    <img
                        src={copyIcon}
                        alt="copy"
                        style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                        onClick={(e) => {
                            e.stopPropagation()
                            copyToClipboard(record.channel_id)
                        }}
                    />
                </span>
            ),
        },
        {
            title: 'Robot',
            dataIndex: 'robot_name',
            search: false,
            ellipsis: true,
            minWidth: 100,
            render: (_, record) => record.robot_name ? record.robot_name : '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            search: false,
            minWidth: 100,
            valueEnum: StatusValueEnum,
            render: (dom, record) => record.channel === WA_PERSONAL ? (() => {
                const unbinding = waUnbinding[record.channel_id]
                if (unbinding) {
                    return <span className={`${waStatusTagClass} ${waStatusClass('DELETING')}`}>{unbinding === 'loading' ? 'Unbinding' : 'Unbind pending'}</span>
                }
                const wa = waAccounts[record.channel_id]
                const st = wa?.account?.status || (wa?.binding?.status === 'pending' ? 'PENDING' : undefined)
                return <span className={`${waStatusTagClass} ${waStatusClass(st)}`}>{st ? waAccountStatusText(st) : '-'}</span>
            })() : (
                <span
                    className={
                        record.status === StatusEnum.online
                            ? styles['status-active']
                            : record.status === StatusEnum.released
                                ? styles['status-released']
                                : styles['status-draft']
                    }
                >
                    {dom}
                </span>
            ),
        },
        {
            title: 'Create Time',
            dataIndex: 'create_time',
            search: false,
            minWidth: 180,
            render: (_, record) => moment(record.create_time).format('YYYY/MM/DD HH:mm:ss'),
        },
        {
            title: 'Update Time',
            dataIndex: 'update_time',
            search: false,
            minWidth: 180,
            render: (_, record) => moment(record.update_time).format('YYYY/MM/DD HH:mm:ss'),
        },
        {
            title: 'Operations',
            valueType: 'option',
            fixed: 'right',
            width: 140,
            minWidth: 140,
            render: (_, record) => record.channel === WA_PERSONAL ? renderWaOperations(record) : [
                <Button
                    key="bind"
                    type="text"
                    onClick={(event) => {
                        event.stopPropagation()
                        setCurrentRecord(record)
                        setBindBotOpen(true)
                    }}
                >
                    <Tooltip title={record.robot_id ? 'Update Bot' : 'Bind Bot'} className={styles.tooltip_custom}>
                        <img
                            src={record.robot_id ? rebindIcon : bindIcon}
                            style={{ width: 14, height: 14, marginLeft: 20, display: 'block' }}
                        />
                    </Tooltip>
                </Button>,
                record.robot_id ? (
                    <Button
                        key="unbind"
                        type="text"
                        onClick={(event) => {
                            event.stopPropagation()
                            handleUnbind(record)
                        }}
                    >
                        <Tooltip title="Unbind Bot" className={styles.tooltip_custom}>
                            <img
                                src={unbindIcon}
                                alt="unbind"
                                style={{ width: 14, height: 14, display: 'block' }}
                            />
                        </Tooltip>
                    </Button>
                ) : null,
                record.status === 'online' ? (
                    <Button
                        key="offline"
                        type="text"
                        onClick={(event) => {
                            event.stopPropagation()
                            handleOffline(record)
                        }}
                    >
                        <Tooltip title="Offline" className={styles.tooltip_custom}>
                            <img
                                src={offlineIcon}
                                alt="offline"
                                style={{ width: 14, height: 14, display: 'block' }}
                            />
                        </Tooltip>
                    </Button>
                ) : null,
                record.status === 'offline' ? (
                    <Button
                        key="online"
                        type="text"
                        onClick={(event) => {
                            event.stopPropagation()
                            handleOnline(record)
                        }}
                    >
                        <Tooltip title="Online" className={styles.tooltip_custom}>
                            <img
                                src={onlineIcon}
                                alt="online"
                                style={{ width: 14, height: 14, display: 'block' }}
                            />
                        </Tooltip>
                    </Button>
                ) : null,
                <Button
                    key="delete"
                    type="text"
                    onClick={(event) => {
                        event.stopPropagation()
                        handleDelete(record)
                    }}
                >
                    <Tooltip title="Delete" className={styles.tooltip_custom}>
                        <img
                            src={require('@/assets/images/delete-new.png')}
                            style={{ width: 16, height: 16, display: 'block' }}
                        />
                    </Tooltip>
                </Button>,
            ],
        },
    ]

    return (
        <div>
            <ProTable<ChannelItem, ChannelListParams>
                actionRef={actionRef}
                formRef={formRef}
                className="channel-full-table"
                rowClassName="cursor-pointer"
                rowKey="id"
                columns={columns}
                params={{ companyName }}
                request={async (params) => {
                    try {
                        if (!companyName) {
                            return { success: true, total: 0, data: [] }
                        }
                        refreshWaAccounts()
                        const res = await getChannelListApi({
                            company: companyName,
                            channel_name: params.channel_name || undefined,
                        })
                        if (res.code === 0) {
                            return {
                                success: true,
                                total: res.data.list.length,
                                data: res.data.list,
                            }
                        }
                        return { success: false, total: 0, data: [] }
                    } catch {
                        message.error('Failed to fetch channel list')
                        return { success: false, total: 0, data: [] }
                    }
                }}
                pagination={{
                    defaultPageSize: 10,
                }}
                search={{
                    layout: 'horizontal',
                    defaultCollapsed: true,
                    searchText: 'Search',
                    className: 'search-tab',
                    resetText: 'Reset',
                    optionRender: () => [
                        <Button
                            key="search"
                            type="primary"
                            onClick={() => {
                                formRef.current?.submit()
                            }}
                        >
                            Search
                        </Button>,
                    ],
                }}
                form={{
                    onValuesChange: () => {
                        if (searchTimerRef.current) {
                            clearTimeout(searchTimerRef.current)
                        }
                        searchTimerRef.current = setTimeout(() => {
                            formRef.current?.submit()
                        }, 500)
                    },
                    syncToUrl: false,
                }}
                toolBarRender={() => [
                    <div style={{ marginTop: '-80px' }}>
                        <Button
                            key="create"
                            type="primary"
                            className={styles['add-channel-button']}
                            onClick={() => setAddChannelOpen(true)}
                        >
                            <img
                                src={addIcon}
                                style={{ width: 16, height: 16, display: 'block' }}
                            />
                            New Channel
                        </Button>
                    </div>
                ]}
                options={false}
            />
            <AddChannel
                open={addChannelOpen}
                onCancel={() => setAddChannelOpen(false)}
                onSuccess={() => {
                    setAddChannelOpen(false)
                    actionRef.current?.reload()
                }}
                onWhatsappCreated={(channelId) => openWaModal(channelId, 'connect')}
            />
            <WhatsappConnect
                open={waModal.open}
                channelId={waModal.channelId}
                initialPhase={waModal.phase}
                onClose={() => {
                    setWaModal({ open: false, channelId: null, phase: 'connect' })
                    actionRef.current?.reload()
                }}
                onChanged={refreshWaAccounts}
            />
            <BindBot
                open={bindBotOpen}
                record={currentRecord}
                onCancel={() => setBindBotOpen(false)}
                onSuccess={() => {
                    setBindBotOpen(false)
                    actionRef.current?.reload()
                }}
            />
        </div>
    )
}

export default Channel
